import { App, Form } from 'antd';
import { FormProviderProps } from 'antd/es/form/context';
import { get } from 'lodash-es';
import { useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { applicationsActions } from '@store/applications/applications.slice';

import { RouteEnum } from '@enum/route.enum';
import { StepEnum } from '@enum/step.enum';
import { useAddApplicationMutation } from '@hook/application/application.hooks';
import { useMutation } from '@hook/common/use-mutation.hook';
import { useStep } from '@hook/common/use-step.hook';
import { useStoreDispatch, useStoreSelector } from '@hook/store/store.hooks';
import { ApplicationInputInterface } from '@interface/application/application.interface';
import { TEAM_ADMIN_ROLES } from '@util/constants/role-names';
import { isExist } from '@util/data/is-exist';

export const useApplicationSetup = () => {
  const dispatch = useStoreDispatch();
  const { onChange, step } = useStep();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const error = useStoreSelector((state) => state.applications.error);
  const { data: accountsStore, isLoading: isLoadingAccounts } = useStoreSelector((state) => state.accounts);

  const accounts = Object.values(accountsStore)
    .filter((account) => TEAM_ADMIN_ROLES.has(account.role))
    .map((account) => ({ value: account.id, label: account.name }));

  const { action, state } = useMutation(
    useAddApplicationMutation(),
    applicationsActions,
    applicationsActions.setApplication,
  );
  const [form] = Form.useForm();
  const onFinish: FormProviderProps['onFormFinish'] = (_, { values }) => {
    const data = { ...values, account: accounts.length === 1 ? get(accounts, '0.value') : values.account };
    void action(data as ApplicationInputInterface);
  };

  useEffect(() => {
    if (state.isSuccess) {
      dispatch(applicationsActions.resetErrors());
      form.resetFields();
      onChange(StepEnum.ApplicationName);
    }
  }, [state.isSuccess]);

  useEffect(
    () => void (!isLoadingAccounts && form.setFieldsValue({ account: get(accounts, '0.value') })),
    [isLoadingAccounts],
  );

  useEffect(() => {
    if (isExist(state.data) && [StepEnum.ApplicationName, StepEnum.Consumer].includes(step)) {
      message.success('Application was successfully added');
      navigate(generatePath(RouteEnum.ApplicationSetupConnectors.path, { applicationId: state.data.data.id }));
    }
  }, [step, state.data]);

  return {
    onFinish,
    form,
    error,
    isLoading: state.isLoading,
    accounts,
  };
};
